/**
 * Activity / Sources - DAM capture-config management UI.
 *
 * OBS-9 (2026-05-26). Surfaces the Tier-A1 capture-configs backend
 * (apps/api-controller/src/modules/activity/capture-configs.ts) that
 * shipped without a UI. Operators (DPO / Compliance Analyst / IT
 * Admin) need to see which data sources are sending DAM events, which
 * are paused, which are erroring - and act on them.
 *
 * Live data sources:
 *   - GET  /v1/activity/capture-configs/summary  → KPI cards
 *   - GET  /v1/activity/capture-configs          → table
 *   - PATCH /v1/activity/capture-configs/:id     → pause / resume
 *   - DELETE /v1/activity/capture-configs/:id    → soft-delete
 *
 * Edge pods self-register via POST /capture-configs/self-register so
 * we deliberately don't expose a "create" form - registration is the
 * pod's job, the UI only manages state of existing rows.
 */

import { useEffect, useState } from 'react';
import {
  Activity as ActivityIcon,
  Database,
  Network,
  ShieldCheck,
  Cpu,
  AlertTriangle,
  Pause,
  Play,
  Trash2,
  Loader2,
  Stethoscope,
  CheckCircle2,
  XCircle,
  Plus,
  X,
} from 'lucide-react';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { KpiCard } from '@/components/viz/KpiCard';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/primitives';
import {
  listActivityCaptureConfigs,
  getActivityCaptureSourcesSummary,
  patchActivityCaptureConfig,
  deleteActivityCaptureConfig,
  testActivityCaptureConfig,
  upsertActivityCaptureConfig,
  listDataSources,
  type ActivityCaptureConfig,
  type ActivityCaptureConfigTestResult,
  type ActivityCaptureSourcesSummary,
  type ActivityCaptureMode,
  type DataSource,
} from '@/lib/api';

const MODE_LABEL: Record<ActivityCaptureMode, string> = {
  native_audit: 'Native audit (pgaudit / etc.)',
  cloud_stream: 'Cloud stream (CloudTrail / etc.)',
  proxy: 'Proxy interception',
  ebpf: 'eBPF kernel sensor',
  network: 'Network tap',
};

const MODE_ICON: Record<ActivityCaptureMode, React.ComponentType<{ className?: string }>> = {
  native_audit: Database,
  cloud_stream: Network,
  proxy: ShieldCheck,
  ebpf: Cpu,
  network: ActivityIcon,
};

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'in the future';
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

function statusVariant(
  s: string,
): 'success' | 'info' | 'destructive' | 'glass' {
  switch (s) {
    case 'active':
      return 'success';
    case 'paused':
      return 'info';
    case 'error':
      return 'destructive';
    default:
      return 'glass';
  }
}

export function ActivitySources() {
  const [summary, setSummary] = useState<ActivityCaptureSourcesSummary | null>(null);
  const [configs, setConfigs] = useState<ActivityCaptureConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // P1-4 (2026-05-26) - per-row test verdict. Map of config id →
  // latest test result (or 'pending' while in-flight).
  const [testResults, setTestResults] = useState<
    Record<string, ActivityCaptureConfigTestResult | 'pending'>
  >({});

  const refresh = async (signal?: AbortSignal): Promise<void> => {
    try {
      const [summaryRes, listRes] = await Promise.allSettled([
        getActivityCaptureSourcesSummary(),
        listActivityCaptureConfigs(),
      ]);
      if (signal?.aborted) return;
      if (summaryRes.status === 'fulfilled') {
        setSummary(summaryRes.value.summary);
      }
      if (listRes.status === 'fulfilled') {
        setConfigs(listRes.value.configs);
      }
      const errs: string[] = [];
      if (summaryRes.status === 'rejected') {
        errs.push(`summary: ${(summaryRes.reason as Error)?.message ?? 'failed'}`);
      }
      if (listRes.status === 'rejected') {
        errs.push(`list: ${(listRes.reason as Error)?.message ?? 'failed'}`);
      }
      setError(errs.length > 0 ? errs.join(' · ') : null);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  const togglePause = async (cfg: ActivityCaptureConfig): Promise<void> => {
    setActingOn(cfg.id);
    try {
      const nextStatus = cfg.status === 'paused' ? 'active' : 'paused';
      await patchActivityCaptureConfig(cfg.id, { status: nextStatus });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'pause/resume failed');
    } finally {
      setActingOn(null);
    }
  };

  const runTest = async (cfg: ActivityCaptureConfig): Promise<void> => {
    setTestResults((prev) => ({ ...prev, [cfg.id]: 'pending' }));
    try {
      const res = await testActivityCaptureConfig(cfg.id);
      setTestResults((prev) => ({ ...prev, [cfg.id]: res }));
    } catch (err) {
      setTestResults((prev) => {
        const copy = { ...prev };
        delete copy[cfg.id];
        return copy;
      });
      setError(err instanceof Error ? err.message : 'test failed');
    }
  };

  const softDelete = async (cfg: ActivityCaptureConfig): Promise<void> => {
    if (!window.confirm(
      `Soft-delete capture config for "${cfg.dataSourceConnectorInstanceId}"? ` +
      `The row is preserved for audit but the edge pod will stop sending events.`,
    )) return;
    setActingOn(cfg.id);
    try {
      await deleteActivityCaptureConfig(cfg.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete failed');
    } finally {
      setActingOn(null);
    }
  };

  // Hide soft-deleted by default in the visible-rows view.
  const visibleConfigs = configs.filter((c) => c.status !== 'deleted');

  return (
    <PageContainer>
      <PageHeader
        title="DAM Sources"
        subtitle="Database activity sources. Add one here, or let an edge-activity pod self-register on first heartbeat - then monitor + manage (pause / resume / test / soft-delete)."
        badges={[
          { label: 'Tier A1', variant: 'glass' },
          { label: 'Edge self-register', variant: 'info' },
        ]}
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add source
          </Button>
        }
      />

      {loading && <LoadingState caption="Loading capture configs…" />}
      {error && !loading && (
        <ErrorState
          title="Some endpoints failed to load"
          detail={error}
          onRetry={() => refresh()}
        />
      )}

      {!loading && (
        <>
          {/* KPI summary */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label="Active"
              value={summary?.totalActive ?? 0}
              icon={ActivityIcon}
              hint="streaming events"
            />
            <KpiCard
              label="Paused"
              value={summary?.totalPaused ?? 0}
              icon={Pause}
              hint="held by operator"
            />
            <KpiCard
              label="Error"
              value={summary?.totalError ?? 0}
              icon={AlertTriangle}
              glow={(summary?.totalError ?? 0) > 0 ? 'critical' : undefined}
              hint="edge pod failing"
            />
            <KpiCard
              label="Avg health score"
              value={summary?.avgHealthScore ?? 0}
              icon={ShieldCheck}
              hint="weighted, 0-100"
              glow={
                summary?.avgHealthScore !== null &&
                summary?.avgHealthScore !== undefined &&
                summary.avgHealthScore < 70
                  ? 'high'
                  : undefined
              }
            />
          </section>

          {/* By-mode breakdown */}
          {summary && summary.byCaptureMode.length > 0 && (
            <section className="mb-6">
              <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                By capture mode
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {summary.byCaptureMode.map((m) => {
                  const mode = m.captureMode as ActivityCaptureMode;
                  const Icon = MODE_ICON[mode] ?? Database;
                  return (
                    <GlassCard key={m.captureMode} className="p-4 flex items-center gap-3">
                      <Icon className="h-5 w-5 text-primary shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">
                          {MODE_LABEL[mode] ?? m.captureMode}
                        </p>
                        <p className="text-xl font-semibold">{m.count}</p>
                      </div>
                    </GlassCard>
                  );
                })}
              </div>
            </section>
          )}

          {/* Capture configs table */}
          <section>
            <div className="flex items-end justify-between mb-3">
              <h2 className="text-xs uppercase tracking-wider text-muted-foreground">
                Active + paused sources ({visibleConfigs.length})
              </h2>
              <Button variant="glass" size="sm" onClick={() => refresh()}>
                Refresh
              </Button>
            </div>

            {visibleConfigs.length === 0 ? (
              <GlassCard className="p-8 text-center space-y-3">
                <p className="text-sm text-muted-foreground">
                  No activity sources yet. <strong className="text-foreground/80">Add one</strong> to declare a
                  database to monitor - or install an edge-activity pod that self-registers on first heartbeat
                  (<code>POST /v1/activity/capture-configs/self-register</code>).
                </p>
                <Button size="sm" onClick={() => setAddOpen(true)}>
                  <Plus className="h-4 w-4" /> Add source
                </Button>
              </GlassCard>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-separate border-spacing-y-1">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2">Source / Connector</th>
                      <th className="text-left px-3 py-2">Mode</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-right px-3 py-2">Health</th>
                      <th className="text-right px-3 py-2">Events / min</th>
                      <th className="text-left px-3 py-2">Last event</th>
                      <th className="text-right px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleConfigs.map((cfg) => {
                      const mode = cfg.captureMode as ActivityCaptureMode;
                      const Icon = MODE_ICON[mode] ?? Database;
                      const busy = actingOn === cfg.id;
                      const healthBadgeVariant =
                        cfg.healthScore === null
                          ? 'glass'
                          : cfg.healthScore >= 80
                            ? 'success'
                            : cfg.healthScore >= 50
                              ? 'info'
                              : 'destructive';
                      // P1-4 - map returns an array of <tr> rows
                      // (main row + optional verdict sub-row).
                      // Outer .flat() merges them into a single
                      // children stream for <tbody>.
                      return [
                        <tr key={cfg.id} className="bg-card/40">
                          <td className="px-3 py-2 font-mono text-xs">
                            {cfg.dataSourceConnectorInstanceId}
                          </td>
                          <td className="px-3 py-2">
                            <span className="inline-flex items-center gap-1.5 text-xs">
                              <Icon className="h-3.5 w-3.5 text-primary" />
                              {MODE_LABEL[mode] ?? cfg.captureMode}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant={statusVariant(cfg.status)}>
                              {cfg.status}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Badge variant={healthBadgeVariant}>
                              {cfg.healthScore ?? '-'}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right tabular">
                            {cfg.eventsPerMinuteRecent ?? '-'}
                          </td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">
                            {relativeTime(cfg.lastEventAt)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="inline-flex gap-1">
                              {/* P1-4 (2026-05-26) - server-side test
                                  runs 3 checks (status/health/freshness)
                                  and shows verdict in a sub-row below. */}
                              <Button
                                variant="glass"
                                size="sm"
                                onClick={() => runTest(cfg)}
                                disabled={testResults[cfg.id] === 'pending'}
                                aria-label="Test this source"
                                title="Run status / health / freshness check"
                              >
                                {testResults[cfg.id] === 'pending' ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Stethoscope className="h-3.5 w-3.5" />
                                )}
                              </Button>
                              <Button
                                variant="glass"
                                size="sm"
                                onClick={() => togglePause(cfg)}
                                disabled={busy || cfg.status === 'error'}
                                aria-label={cfg.status === 'paused' ? 'Resume' : 'Pause'}
                              >
                                {busy ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : cfg.status === 'paused' ? (
                                  <Play className="h-3.5 w-3.5" />
                                ) : (
                                  <Pause className="h-3.5 w-3.5" />
                                )}
                              </Button>
                              <Button
                                variant="glass"
                                size="sm"
                                onClick={() => softDelete(cfg)}
                                disabled={busy}
                                aria-label="Soft-delete"
                                className="hover:text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>,
                        // P1-4 (2026-05-26) - verdict sub-row only
                        // renders when the operator has clicked Test
                        // for this row. Server-side check: each of
                        // status / health / freshness gets ✓ or ⚠
                        // with a one-line detail.
                        (() => {
                          const res = testResults[cfg.id];
                          if (!res || res === 'pending') return null;
                          return (
                            <tr key={`${cfg.id}-test`} className="bg-card/20">
                              <td colSpan={7} className="px-3 py-2 text-[11px]">
                                <div className="flex items-start gap-3">
                                  {res.ok ? (
                                    <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                                  )}
                                  <div className="flex-1 space-y-1">
                                    <div className={res.ok ? 'text-success' : 'text-destructive'}>
                                      {res.summary}
                                    </div>
                                    <ul className="flex flex-wrap gap-x-4 gap-y-0.5 text-muted-foreground">
                                      {res.checks.map((c) => (
                                        <li key={c.id} className="inline-flex items-center gap-1.5">
                                          {c.ok ? (
                                            <CheckCircle2 className="h-3 w-3 text-success" />
                                          ) : (
                                            <AlertTriangle className="h-3 w-3 text-warning" />
                                          )}
                                          <span className="font-medium">{c.id}:</span>
                                          <span>{c.detail}</span>
                                        </li>
                                      ))}
                                    </ul>
                                    <div className="text-[10px] text-muted-foreground/70">
                                      Tested {new Date(res.testedAt).toLocaleTimeString()}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })(),
                      ];
                    }).flat()}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {addOpen && (
        <AddSourceModal
          onClose={() => setAddOpen(false)}
          onAdded={() => { setAddOpen(false); void refresh(); }}
        />
      )}
    </PageContainer>
  );
}

// ── Add-source modal - registers a DAM capture source via the existing
//    POST /v1/activity/capture-configs (upsert). Unblocks DAM-only customers
//    who haven't deployed a self-registering edge pod. (Phase 1A.)
const ADD_MODES: ReadonlyArray<{ value: ActivityCaptureMode; label: string; hint: string }> = [
  { value: 'native_audit', label: 'Native audit log', hint: 'pgaudit · MSSQL XE · Oracle Unified Audit - least invasive, monitor-only' },
  { value: 'cloud_stream', label: 'Cloud event stream', hint: 'CloudTrail · Snowflake ACCESS_HISTORY · BigQuery JOBS' },
  { value: 'proxy', label: 'SQL proxy sidecar', hint: 'in-line proxy - required for DDR real-time blocking' },
  { value: 'ebpf', label: 'eBPF kernel sensor', hint: 'Tetragon - opt-in, needs kernel approval' },
  { value: 'network', label: 'Network tap', hint: 'mirrored traffic - legacy on-prem' },
];

function AddSourceModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [form, setForm] = useState({
    sourceId: '', displayName: '', captureMode: 'native_audit' as ActivityCaptureMode,
    vendor: 'postgres', host: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [registry, setRegistry] = useState<DataSource[]>([]);
  const ready = form.sourceId.trim().length > 0;

  // Phase 2 wiring - offer the shared Data Source registry (DAM-relevant rows)
  // so DAM consumes a source registered once, instead of re-typing an id.
  useEffect(() => {
    let alive = true;
    void listDataSources()
      .then((r) => {
        if (alive) setRegistry(r.sources.filter((s) => String(s.category).startsWith('dam_')));
      })
      .catch(() => {
        /* registry optional - manual entry still works */
      });
    return () => {
      alive = false;
    };
  }, []);

  function pickRegistered(id: string): void {
    const s = registry.find((x) => x.id === id);
    if (!s) return;
    const mode: ActivityCaptureMode =
      s.category === 'dam_cloud_stream' ? 'cloud_stream' : s.category === 'dam_proxy' ? 'proxy' : 'native_audit';
    setForm((f) => ({ ...f, sourceId: s.id, displayName: s.displayName, captureMode: mode, vendor: s.vendor ?? f.vendor }));
  }

  async function onSubmit() {
    setBusy(true); setErr(null);
    try {
      const configuration: Record<string, unknown> = {
        displayName: form.displayName.trim() || form.sourceId.trim(),
        vendor: form.vendor.trim(),
        ...(form.host.trim() ? { host: form.host.trim() } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        addedVia: 'ui',
      };
      await upsertActivityCaptureConfig({
        dataSourceConnectorInstanceId: form.sourceId.trim(),
        captureMode: form.captureMode,
        configuration,
        status: 'active',
      });
      onAdded();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not add source');
    } finally {
      setBusy(false);
    }
  }

  const inputCls = 'w-full bg-muted/30 border border-border/50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary/60';
  const lab = 'text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block';
  const mode = ADD_MODES.find((m) => m.value === form.captureMode);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Database className="h-4 w-4 text-primary" /> Add activity source</DialogTitle>
          <DialogDescription>Declare a database to monitor. The edge-activity pod configured with this source id streams hashed activity events here - raw SQL never leaves your network.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {registry.length > 0 && (
            <div>
              <label className={lab}>Use a registered data source</label>
              <select className={inputCls} value="" onChange={(e) => e.target.value && pickRegistered(e.target.value)}>
                <option value="">- pick from the Data Sources registry -</option>
                {registry.map((s) => (
                  <option key={s.id} value={s.id}>{s.displayName}</option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground mt-1">
                Registered once in Settings &amp; Admin → Data Sources. Or define a new one below.
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={lab}>Source id</label>
              <input className={inputCls} placeholder="acme-prod-postgres" value={form.sourceId} onChange={(e) => setForm((f) => ({ ...f, sourceId: e.target.value }))} />
            </div>
            <div>
              <label className={lab}>Display name</label>
              <input className={inputCls} placeholder="ACME Prod (Postgres)" value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className={lab}>Capture mode</label>
            <select className={inputCls} value={form.captureMode} onChange={(e) => setForm((f) => ({ ...f, captureMode: e.target.value as ActivityCaptureMode }))}>
              {ADD_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            {mode && <p className="text-[11px] text-muted-foreground mt-1">{mode.hint}</p>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={lab}>Vendor</label>
              <input className={inputCls} placeholder="postgres" value={form.vendor} onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))} />
            </div>
            <div>
              <label className={lab}>Host (optional)</label>
              <input className={inputCls} placeholder="db.internal:5432" value={form.host} onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className={lab}>Notes (optional)</label>
            <input className={inputCls} placeholder="Treasury BU · SEBI-scoped" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          {err && (
            <div className="flex items-start gap-2 rounded-md border border-sev-high/40 bg-sev-high/5 p-3 text-xs">
              <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />
              <div><div className="font-medium">Could not add source</div><div className="text-muted-foreground mt-0.5">{err}</div></div>
            </div>
          )}
          <div className="rounded-md bg-muted/20 p-2.5 text-[11px] text-muted-foreground flex items-start gap-2">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5 text-success" />
            Registers the source. Point an edge-activity pod at this controller with the same source id + capture mode (or let it self-register on first heartbeat) to start streaming.
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" onClick={onSubmit} disabled={!ready || busy}>
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Adding…</> : <><Plus className="h-4 w-4" /> Add source</>}
            </Button>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={onClose}><X className="h-4 w-4" /> Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
